import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { Image } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';
import { useThemeColors } from '@/providers/ThemeProvider';
import { ChatWithDetails } from '../types';
import {
  getChatDisplayName,
  getChatAvatar,
  formatMessagePreview,
  getChatStyle,
} from '../utils/chatHelpers';

interface ChatListItemProps {
  chat: ChatWithDetails;
  currentUserId: string;
  activeWeekendId?: string;
  onLongPress?: (chat: ChatWithDetails) => void;
  onPress?: (chat: ChatWithDetails) => void;
}

export const ChatListItem: React.FC<ChatListItemProps> = ({
  chat,
  currentUserId,
  activeWeekendId,
  onLongPress,
  onPress,
}) => {
  const colors = useThemeColors();
  const displayName = getChatDisplayName(chat, currentUserId);
  const avatarUrl = getChatAvatar(chat, currentUserId);
  const style = getChatStyle(chat, activeWeekendId);
  const lastMessage = chat.last_message;

  // Determine background color based on chat type
  const isGroupChat = chat.type === 'group' || chat.type === 'team';
  const itemBackgroundColor = isGroupChat 
    ? colors.primaryLight 
    : colors.surface;

  /**
   * Format message time, handling invalid dates and showing relative dates
   */
  const formatMessageTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '';
      }
      
      const now = new Date();
      const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        // Today - show time
        return date.toLocaleTimeString('nl-NL', {
          hour: '2-digit',
          minute: '2-digit',
        });
      } else if (diffDays === 1) {
        return 'Gisteren';
      } else if (diffDays < 7) {
        // Show day name
        return date.toLocaleDateString('nl-NL', { weekday: 'short' });
      } else {
        // Show date
        return date.toLocaleDateString('nl-NL', {
          day: '2-digit',
          month: '2-digit',
        });
      }
    } catch {
      return '';
    }
  };

  return (
    <TouchableOpacity
      onPress={() => onPress?.(chat)}
      onLongPress={() => onLongPress?.(chat)}
      activeOpacity={0.7}
      className="rounded-2xl p-4 mb-3"
      style={{
        backgroundColor: itemBackgroundColor,
        borderLeftWidth: 4,
        borderLeftColor: style.borderColor,
        opacity: style.opacity,
      }}
    >
      <View className="flex-row items-center">
        {/* Avatar */}
        <View className="relative mr-3">
          {avatarUrl ? (
            <View className="w-14 h-14 rounded-full overflow-hidden">
              <Image
                source={{ uri: avatarUrl }}
                style={{ width: 56, height: 56 }}
                contentFit="cover"
              />
            </View>
          ) : (
            <View 
              className="w-14 h-14 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.primaryLight }}
            >
              <Ionicons
                name={chat.type === 'direct' ? 'person' : 'people'}
                size={28}
                color={colors.primary}
              />
            </View>
          )}

          {/* Unread badge */}
          {chat.unread_count > 0 && (
            <View 
              className="absolute -top-1 -right-1 rounded-full min-w-[20px] h-5 items-center justify-center px-1"
              style={{ backgroundColor: colors.error }}
            >
              <Text className="text-white text-xs font-bold">
                {chat.unread_count > 99 ? '99+' : chat.unread_count}
              </Text>
            </View>
          )}
        </View>

        {/* Content */}
        <View className="flex-1">
          {/* Name and time */}
          <View className="flex-row items-center justify-between mb-1">
            <View className="flex-row items-center flex-1">
              <Text
                className="text-base font-semibold"
                style={{ color: colors.text }}
                numberOfLines={1}
              >
                {displayName}
              </Text>
              {style.badge && (
                <View 
                  className="ml-2 px-2 py-0.5 rounded"
                  style={{ backgroundColor: colors.warningLight }}
                >
                  <Text className="text-xs" style={{ color: colors.warning }}>
                    {style.badge}
                  </Text>
                </View>
              )}
              {chat.is_muted && (
                <Ionicons
                  name="notifications-off"
                  size={14}
                  color={colors.textTertiary}
                  style={{ marginLeft: 4 }}
                />
              )}
            </View>
            {(lastMessage?.created_at || chat.last_message_at) && (
              <Text className="text-xs ml-2" style={{ color: colors.textSecondary }}>
                {formatMessageTime(lastMessage?.created_at || chat.last_message_at || '')}
              </Text>
            )}
          </View>

          {/* Last message preview */}
          {lastMessage && (
            <View className="flex-row items-center">
              <Text
                className="text-sm flex-1"
                style={{ 
                  color: chat.unread_count > 0 ? colors.text : colors.textSecondary,
                  fontWeight: chat.unread_count > 0 ? '600' : '400'
                }}
                numberOfLines={1}
              >
                {lastMessage.sender_id === currentUserId && 'Jij: '}
                {formatMessagePreview(lastMessage)}
              </Text>
              {chat.unread_count === 0 && (
                <Ionicons
                  name="checkmark-done"
                  size={16}
                  color={colors.success}
                  style={{ marginLeft: 4 }}
                />
              )}
            </View>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
};
